<template>
  <div class="mileage-status-module" :class="{ 'collapsed': isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse" :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-pulse" /> {{ langMap.dashboard_runningStatus[lang] }}
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>
    
    <div class="devices-container" v-show="!isCollapsed">
      <!-- 空数据状态 -->
      <div v-if="!hasData" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-pulse" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      
      <!-- 正常数据 -->
      <div v-else class="devices-container-local">
        <!-- 里程数据卡片 -->
        <div v-if="hasMileageData" class="device-card full-width">
          <div class="device-header">
            <Icon type="ios-speedometer" class="device-icon" />
            <span class="device-name-main">{{ langMap.dashboard_mileage_category[lang] }}</span>
          </div>
          <div class="device-params params-grid">
            <div v-if="mileageData.totalPowerOnSeconds !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_totalPowerOnSeconds[lang] }}</div>
              <div class="param-value">{{ secondsToHours(mileageData.totalPowerOnSeconds) }}<span class="unit">h</span></div>
            </div>
            <div v-if="mileageData.currentPowerOnSeconds !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_currentPowerOnSeconds[lang] }}</div>
              <div class="param-value">{{ secondsToHours(mileageData.currentPowerOnSeconds) }}<span class="unit">h</span></div>
            </div>
            <div v-if="mileageData.totalDrivingDistance !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_totalDrivingDistance[lang] }}</div>
              <div class="param-value">{{ formatRawNumber(mileageData.totalDrivingDistance) }}<span class="unit">m</span></div>
            </div>
            <div v-if="mileageData.currentDrivingDistance !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_currentDrivingDistance[lang] }}</div>
              <div class="param-value">{{ formatRawNumber(mileageData.currentDrivingDistance) }}<span class="unit">m</span></div>
            </div>
            <div v-if="mileageData.totalPowerOnTimes !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_totalPowerOnTimes[lang] }}</div>
              <div class="param-value">{{ formatRawNumber(mileageData.totalPowerOnTimes) }}<span class="unit">次</span></div>
            </div>
          </div>
        </div>

        <!-- 甲烷数据卡片 -->
        <div v-if="hasMethaneData" class="device-card full-width">
          <div class="device-header">
            <Icon type="ios-flame" class="device-icon" />
            <span class="device-name-main">{{ langMap.dashboard_methane_category[lang] }}</span>
          </div>
          <div class="device-params params-grid">
            <div v-if="methaneData.methaneCon !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_methane_concentration[lang] }}</div>
              <div class="param-value">{{ methaneData.methaneCon }}</div>
            </div>
            <div v-if="methaneData.lightInt !== undefined" class="param-item">
              <div class="param-label">{{ langMap.dashboard_light_intensity[lang] }}</div>
              <div class="param-value">{{ methaneData.lightInt }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'
import collapsibleMixin from './collapsible-mixin'

export default {
  name: 'MileageStatusModule',
  mixins: [collapsibleMixin],
  props: {
    mileageData: {
      type: Object,
      default: () => ({})
    },
    methaneData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      langMap
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    
    hasMileageData() {
      return this.mileageData && Object.keys(this.mileageData).length > 0;
    },
    hasMethaneData() {
      return this.methaneData && Object.keys(this.methaneData).length > 0;
    },
    hasData() {
      return this.hasMileageData || this.hasMethaneData;
    }
  },
  methods: {
    // 格式化原始数值（不转换单位，直接显示）
    formatRawNumber(number) {
      if (!number && number !== 0) return '-'
      
      // 处理字符串格式的数字
      const num = parseFloat(number)
      if (isNaN(num)) return '-'
      
      // 直接返回数值，保留小数点（如果有）
      return num.toString()
    },
    
    // 将秒转换为小时
    secondsToHours(seconds) {
      if (!seconds && seconds !== 0) return '-'
      
      const num = parseFloat(seconds)
      if (isNaN(num)) return '-'
      
      // 秒转小时，保留2位小数
      const hours = (num / 3600).toFixed(2)
      return hours
    }
  }
}
</script>

<style lang="less" scoped>
.mileage-status-module {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;
  
  &.collapsed {
    flex: 0 0 auto;
  }
  
  &.collapsed {
    flex: 0 0 auto;
  }
  
  .sci-fi-title {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95));
      border-bottom-color: rgba(0, 240, 255, 0.8);
    }
    
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
        margin-left: auto;
      }
    }
    
    .title-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover .title-bg {
      opacity: 1;
    }
    
    .title-decoration {
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00f0ff, transparent);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
    }
  }
  
  // 扫描线效果
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 240, 255, 1) 50%, 
      transparent
    );
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
    z-index: 5;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.1),
      transparent
    );
    animation: dataflow 3s ease-in-out infinite;
  }

  .devices-container {
    flex: 1;
    display: flex;
    min-height: 0;

    // 无数据状态
    .no-data-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      min-height: 200px;
      gap: 24px;
      padding: 24px;

      .hologram-container {
        position: relative;
        width: 120px;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;

        .hologram-ring {
          position: absolute;
          border: 2px solid rgba(0, 240, 255, 0.4);
          border-radius: 50%;
          animation: pulse-ring 2s ease-in-out infinite;

          &.ring-1 {
            width: 120px;
            height: 120px;
            animation-delay: 0s;
          }

          &.ring-2 {
            width: 90px;
            height: 90px;
            animation-delay: 0.3s;
          }

          &.ring-3 {
            width: 60px;
            height: 60px;
            animation-delay: 0.6s;
          }
        }

        .hologram-icon {
          position: relative;
          z-index: 1;
          font-size: 48px;
          color: #00f0ff;
          text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
            0 0 40px rgba(0, 240, 255, 0.6),
            0 0 60px rgba(0, 240, 255, 0.4);
          animation: float 3s ease-in-out infinite;

          i {
            display: block;
          }
        }
      }

      .no-data-text {
        text-align: center;

        .main-text {
          font-size: 16px;
          font-weight: 600;
          color: #00f0ff;
          text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
          margin-bottom: 8px;
          letter-spacing: 1px;
        }

        .sub-text {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          animation: pulse-text 2s ease-in-out infinite;
        }
      }
    }

    .devices-container-local {
      flex: 1;
      padding: 8px;
      display: flex;
      flex-direction: column; /* 改为列布局 */
      gap: 8px;
      overflow-y: auto;
      overflow-x: hidden;
      min-height: 0;
      align-content: flex-start;

      &::-webkit-scrollbar {
        width: 4px;
      }
      
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      
      &::-webkit-scrollbar-thumb {
        background: rgba(0, 240, 255, 0.3);
        border-radius: 2px;
      }
    }
  }

  .device-card {
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.7), rgba(0, 20, 45, 0.9));
    border: 1px solid rgba(0, 240, 255, 0.4);
    border-radius: 8px;
    padding: 4px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    
    &.full-width {
      flex: 0 0 auto; /* 自动高度 */
    }

    .device-header {
      display: flex;
      align-items: center;
      gap: 8px; /* 增加间距 */
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.3);
      
      .device-icon {
        font-size: 16px;
        color: #00f0ff;
      }
      
      .device-name-main {
        font-size: 12px;
        font-weight: 600;
        color: #00f0ff;
      }
    }

      .device-params.params-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 3px;
        
        .param-item {
          background: linear-gradient(135deg, rgba(0, 240, 255, 0.12), rgba(0, 180, 255, 0.08));
          border: 1px solid rgba(0, 240, 255, 0.35);
          border-radius: 6px;
          padding: 2px;
          text-align: center;
          position: relative;
          overflow: hidden;
          flex: 0 0 ~"calc(50% - 51px)"; /* 每行3个 */
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2),
                      inset 0 1px 0 rgba(0, 240, 255, 0.15);
          min-height: 60px;

          .param-label {
            font-size: 10px;
            color: rgba(255, 255, 255, 0.65);
            margin-bottom: 4px;
          }
          
          .param-value {
            font-size: 14px;
            font-weight: 700;
            color: #fff;
            font-family: 'Consolas', 'Monaco', monospace;

            .unit {
              font-size: 10px;
              color: rgba(0, 240, 255, 1);
              margin-left: 2px;
              font-weight: 600;
            }
          }
        }
      }
  }
}

// 动画定义
@keyframes scanline {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(500px);
    opacity: 0;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes dataflow {
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}

</style>
